import Reflux from "reflux";
import { SystemClusterConfig, SystemMessageProcessors, SystemUrlAllowlist } from "@graylog/server-api";
import i18n from "i18n";
import { qualifyUrl } from "util/URLUtils";
import fetch from "logic/rest/FetchProvider";
import ApiRoutes from "routing/ApiRoutes";
import UserNotification from "util/UserNotification";
import { singletonStore, singletonActions } from "logic/singleton";
export const ConfigurationsActions = singletonActions(
  "core.Configuration",
  () => Reflux.createActions({
    list: { asyncResult: true },
    listSearchesClusterConfig: { asyncResult: true },
    listMessageProcessorsConfig: { asyncResult: true },
    listEventsClusterConfig: { asyncResult: true },
    listIndexSetsDefaultsClusterConfig: { asyncResult: true },
    listAllowListConfig: { asyncResult: true },
    listPermissionsConfig: { asyncResult: true },
    listUserConfig: { asyncResult: true },
    listPasswordComplexityConfig: { asyncResult: true },
    update: { asyncResult: true },
    updateAllowlist: { asyncResult: true },
    updateIndexSetDefaults: { asyncResult: true },
    updateMessageProcessorsConfig: { asyncResult: true }
  })
);
const urlPrefix = ApiRoutes.ClusterConfigResource.config().url;
export const ConfigurationsStore = singletonStore(
  "core.Configuration",
  () => Reflux.createStore({
    listenables: [ConfigurationsActions],
    configuration: {},
    searchesClusterConfig: {},
    eventsClusterConfig: {},
    indexSetsDefaultConfig: {},
    getInitialState() {
      return this.getState();
    },
    getState() {
      return {
        configuration: this.configuration,
        searchesClusterConfig: this.searchesClusterConfig,
        eventsClusterConfig: this.eventsClusterConfig,
        indexSetsDefaultConfig: this.indexSetsDefaultConfig
      };
    },
    propagateChanges() {
      this.trigger(this.getState());
    },
    _url(path) {
      return qualifyUrl(urlPrefix + path);
    },
    list(configType) {
      const promise = SystemClusterConfig.read(configType);
      promise.then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.list.promise(promise);
    },
    listSearchesClusterConfig() {
      const promise = SystemClusterConfig.read("org.graylog2.indexer.searches.SearchesClusterConfig").then(
        (response) => {
          this.searchesClusterConfig = response;
          this.propagateChanges();
          return response;
        }
      );
      ConfigurationsActions.listSearchesClusterConfig.promise(promise);
    },
    listMessageProcessorsConfig(configType) {
      const promise = SystemMessageProcessors.config().then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.listMessageProcessorsConfig.promise(promise);
    },
    listAllowListConfig(configType) {
      const promise = SystemUrlAllowlist.get().then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.listAllowListConfig.promise(promise);
    },
    listPermissionsConfig(configType) {
      const promise = SystemClusterConfig.read(configType).then((response) => {
        this.configuration = {
          ...this.configuration,
          // default values bellow should be the same in backend.
          [configType]: response || {
            allow_sharing_with_everyone: true,
            allow_sharing_with_users: true
          }
        };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.listPermissionsConfig.promise(promise);
    },
    listUserConfig(configType) {
      const promise = SystemClusterConfig.read(configType).then((response) => {
        this.configuration = {
          ...this.configuration,
          // default values bellow should be the same in backend.
          [configType]: response || {
            enable_global_session_timeout: false,
            global_session_timeout_interval: "PT1H"
          }
        };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.listUserConfig.promise(promise);
    },
    listPasswordComplexityConfig(configType) {
      const promise = SystemClusterConfig.read(configType).then((response) => {
        this.configuration = {
          ...this.configuration,
          [configType]: response || {
            min_length: 6,
            require_uppercase: false,
            require_lowercase: false,
            require_numbers: false,
            require_special_chars: false
          }
        };
        this.propagateChanges();
        return response;
      });
      ConfigurationsActions.listPasswordComplexityConfig.promise(promise);
    },
    listEventsClusterConfig() {
      const promise = SystemClusterConfig.read("org.graylog.events.configuration.EventsConfiguration").then(
        (response) => {
          this.eventsClusterConfig = response;
          this.propagateChanges();
          return response;
        }
      );
      ConfigurationsActions.listEventsClusterConfig.promise(promise);
    },
    listIndexSetsDefaultsClusterConfig() {
      const promise = SystemClusterConfig.read("org.graylog2.configuration.IndexSetsDefaultConfiguration").then(
        (response) => {
          this.indexSetsDefaultConfig = response;
          this.propagateChanges();
          return response;
        }
      );
      ConfigurationsActions.listIndexSetsDefaultsClusterConfig.promise(promise);
    },
    updateIndexSetDefaults(configType, config) {
      const promise = fetch("PUT", qualifyUrl("/system/indices/index_set_defaults"), config);
      promise.then(
        () => {
          this.configuration = { ...this.configuration, [configType]: config };
          this.propagateChanges();
          UserNotification.success(i18n.t("notifications.index_defaults_success", { ns: "configurations" }));
          return config;
        },
        (error) => {
          UserNotification.error(
            error.additional.body.message,
            i18n.t("notifications.index_defaults_failed_title", { ns: "configurations" })
          );
        }
      );
      ConfigurationsActions.updateIndexSetDefaults.promise(promise);
    },
    update(configType, config) {
      const promise = fetch("PUT", this._url(`/${configType}`), config);
      promise.then(
        (response) => {
          this.configuration = { ...this.configuration, [configType]: response };
          this.propagateChanges();
          UserNotification.success(i18n.t("notifications.update_success", { ns: "configurations" }));
          return response;
        },
        (error) => {
          UserNotification.error(
            i18n.t("notifications.search_update_failed", { ns: "configurations", error }),
            i18n.t("notifications.search_update_failed_title", { ns: "configurations", configType })
          );
        }
      );
      ConfigurationsActions.update.promise(promise);
    },
    updateAllowlist(configType, config) {
      const promise = fetch("PUT", qualifyUrl("/system/urlallowlist"), config);
      promise.then(
        () => {
          this.configuration = { ...this.configuration, [configType]: config };
          this.propagateChanges();
          UserNotification.success(i18n.t("notifications.allowlist_update_success", { ns: "configurations" }));
          return config;
        },
        (error) => {
          UserNotification.error(
            i18n.t("notifications.allowlist_update_failed", { ns: "configurations", error }),
            i18n.t("notifications.allowlist_update_failed_title", { ns: "configurations", configType })
          );
        }
      );
      ConfigurationsActions.updateAllowlist.promise(promise);
    },
    updateMessageProcessorsConfig(configType, config) {
      const promise = fetch("PUT", qualifyUrl("/system/messageprocessors/config"), config);
      promise.then(
        (response) => {
          this.configuration = { ...this.configuration, [configType]: response };
          this.propagateChanges();
          UserNotification.success(i18n.t("notifications.update_success", { ns: "configurations" }));
          return response;
        },
        (error) => {
          UserNotification.error(
            i18n.t("notifications.processors_update_failed", { ns: "configurations", error }),
            i18n.t("notifications.processors_update_failed_title", { ns: "configurations", configType })
          );
        }
      );
      ConfigurationsActions.updateMessageProcessorsConfig.promise(promise);
    }
  })
);
